import { create } from 'zustand';
import { fetchRecords, queryRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from "../api/fields";

const FFT = FIELDS.formTemplates;
const FFR = FIELDS.formResponses;

function mapFormTemplate(r: Record<string, unknown>): FormTemplate {
  const id = r.record_id as number;
  const name = String(r[FFT.templateName] ?? '');
  const schema = String(r[FFT.schema] ?? '{}');
  // isPublic/publicSlug are stored inside the schema JSON blob
  let isPublic = 'false';
  let publicSlug = '';
  try {
    const parsed = JSON.parse(schema);
    isPublic = (parsed.isPublic === true || parsed.isPublic === 'true') ? 'true' : 'false';
    publicSlug = String(parsed.publicSlug || '');
  } catch { /* ignore */ }
  return {
    id, Rid: id,
    templateName: name,
    title: name,
    description: String(r[FFT.description] ?? ''),
    schema,
    schemaJson: schema,
    status: String(r[FFT.status] ?? 'draft'),
    module: '',
    incidentId: '',
    isPublic,
    publicSlug,
    creatorName: String(r[FFT.creatorName] ?? ''),
    responseCount: Number(r[FFT.responseCount]) || 0,
    datecreated: '',
  };
}

function mapFormResponse(r: Record<string, unknown>): FormResponse {
  const id = r.record_id as number;
  const responseData = String(r[FFR.responseData] ?? '');
  return {
    id, Rid: id,
    templateId: Number(r[FFR.templateId]) || 0,
    templateName: '',
    incidentId: '',
    respondentName: String(r[FFR.respondentName] ?? ''),
    respondentEmail: String(r[FFR.respondentEmail] ?? ''),
    answersJson: responseData,
    responseData,
    status: String(r[FFR.status] ?? 'new'),
    submittedAt: String(r[FFR.submittedAt] ?? ''),
    notes: '',
  };
}

interface FormTemplate { id: number; Rid?: number; title: string; description: string; schemaJson: string; status: string; responseCount: number; templateName?: string; datecreated?: string; module?: string; incidentId?: string; isPublic?: string; publicSlug?: string; stepsJson?: string; [k: string]: unknown; }
interface FormResponse { id: number; templateId: number; respondentName: string; respondentEmail: string; answersJson: string; status: string; submittedAt: string; templateName?: string; incidentId?: string; [k: string]: unknown; }

interface FormsState {
  templates: FormTemplate[]; responses: FormResponse[]; loading: boolean;
  fetchTemplates: () => Promise<void>; fetchResponses: (templateId?: number) => Promise<void>;
  createTemplate: (data: Record<string, unknown>) => Promise<number | null>;
  updateTemplate: (id: number, data: Record<string, unknown>) => Promise<void>;
  submitResponse: (data: Record<string, unknown>) => Promise<number | null>;
  updateResponseStatus: (id: number, status: string) => Promise<void>;

  // V1 compat aliases
  isLoading: boolean;
  loadTemplates: () => Promise<void>;
  loadResponses: (templateId?: any) => Promise<void>;
  loadFormTemplates: () => Promise<void>;
  loadTemplate: (id: any) => Promise<FormTemplate | null>;
  loadAll: () => Promise<void>;
  updateResponse: (id: any, data: any) => Promise<void>;
  deleteTemplate: (id: any) => Promise<void>;
}

export const useFormsStore = create<FormsState>((set, get) => ({
  templates: [], responses: [], loading: false,
  fetchTemplates: async () => { try { const r = await fetchRecords<FormTemplate>(TABLES.formTemplates, { limit: 200 }); set({ templates: r.records.map(mapFormTemplate) }); } catch (e) { console.error('[Forms]', e); } },
  fetchResponses: async (templateId) => {
    try {
      const opts = templateId ? { filters: [{ field: FFR.templateId, operator: 'eq' as const, value: templateId }] } : {};
      const r = await queryRecords<FormResponse>(TABLES.formResponses, { limit: 500, sort: { field: 'created_at', direction: 'desc' }, ...opts });
      set({ responses: r.records.map(mapFormResponse) });
    } catch (e) { console.error('[Forms]', e); }
  },
  createTemplate: async (data) => {
    const p: Record<string,unknown> = {};
    if (data.title || data.templateName) p[FFT.templateName] = data.title || data.templateName;
    if (data.description) p[FFT.description] = data.description;
    if (data.schemaJson || data.schema) p[FFT.schema] = data.schemaJson || data.schema;
    if (data.status) p[FFT.status] = data.status || 'draft';
    if (data.creatorName) p[FFT.creatorName] = data.creatorName;
    try { const r = await createRecord<Record<string, unknown>>(TABLES.formTemplates, p); await get().fetchTemplates(); return (r.record_id ?? r.id) as number; } catch (e) { console.error('[Forms]', e); return null; }
  },
  updateTemplate: async (id, data) => {
    const p: Record<string,unknown> = {};
    if (data.title !== undefined || data.templateName !== undefined) p[FFT.templateName] = data.title ?? data.templateName;
    if (data.description !== undefined) p[FFT.description] = data.description;
    if (data.schemaJson !== undefined || data.schema !== undefined) p[FFT.schema] = data.schemaJson ?? data.schema;
    if (data.status !== undefined) p[FFT.status] = data.status;
    try { await updateRecord(TABLES.formTemplates, id, p); await get().fetchTemplates(); } catch (e) { console.error('[Forms]', e); }
  },
  submitResponse: async (data) => {
    const p: Record<string,unknown> = { [FFR.submittedAt]: new Date().toISOString(), [FFR.status]: data.status || 'new' };
    if (data.templateId) p[FFR.templateId] = data.templateId;
    if (data.respondentName) p[FFR.respondentName] = data.respondentName;
    if (data.respondentEmail) p[FFR.respondentEmail] = data.respondentEmail;
    if (data.answersJson || data.responseData) p[FFR.responseData] = data.answersJson ?? data.responseData;
    try { const r = await createRecord<Record<string, unknown>>(TABLES.formResponses, p); return (r.record_id ?? r.id) as number; } catch (e) { console.error('[Forms]', e); return null; }
  },
  updateResponseStatus: async (id, status) => { try { await updateRecord(TABLES.formResponses, id, { [FFR.status]: status }); await get().fetchResponses(); } catch (e) { console.error('[Forms]', e); } },

  // V1 compat aliases
  isLoading: false,
  loadTemplates: async () => { await (get() as any).fetchTemplates(); },
  loadResponses: async (templateId?: any) => { await (get() as any).fetchResponses(templateId); },
  loadFormTemplates: async () => { await (get() as any).fetchTemplates(); },
  loadTemplate: async (id: any) => { return (get() as any).templates?.find((t: any) => t.id === id) || null; },
  loadAll: async () => { await (get() as any).fetchTemplates(); await (get() as any).fetchResponses(); },
  updateResponse: async (id: any, data: any) => { await (get() as any).updateResponseStatus(id, data.status ?? data); },
  deleteTemplate: async (id: any) => { try { await deleteRecord(TABLES.formTemplates, id); await (get() as any).fetchTemplates(); } catch (e) { console.error('[Forms]', e); } },
}));
